import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AdminLogsService, ApiLogEvent } from 'services/admin-logs.service';

interface ViewLogLine {
  type: 'info' | 'normal' | 'success' | 'warning' | 'error';
  text: string;
  time: string;
}

@Component({
  selector: 'app-admin-logs',
  templateUrl: './admin-logs.component.html',
  styleUrls: ['./admin-logs.component.scss'],
})
export class AdminLogsComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('terminalBottom') terminalBottom?: ElementRef<HTMLDivElement>;

  logs: ViewLogLine[] = [];
  filteredLogs: ViewLogLine[] = [];

  connected = false;
  paused = false;
  autoScroll = true;
  filterText = '';

  private sub?: Subscription;
  private shouldScroll = false;

  constructor(private adminLogsService: AdminLogsService) {}

  ngOnInit(): void {
    this.connect();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.autoScroll && !this.paused) {
      this.terminalBottom?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });

      this.shouldScroll = false;
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  trackByIndex(index: number): number {
    return index;
  }

  connect(): void {
    this.disconnect();

    this.connected = true;

    this.sub = this.adminLogsService.streamApiLogs().subscribe({
      next: (event) => this.handleEvent(event),
      error: () => {
        this.connected = false;
        this.addLine({
          type: 'error',
          text: 'Se perdió la conexión con el stream de logs.',
          time: this.getTime(),
        });
      },
      complete: () => {
        this.connected = false;
      },
    });
  }

  disconnect(): void {
    this.sub?.unsubscribe();
    this.sub = undefined;
    this.adminLogsService.close();
    this.connected = false;
  }

  togglePause(): void {
    this.paused = !this.paused;
  }

  clearLogs(): void {
    this.logs = [];
    this.applyFilter();
  }

  onFilterChange(): void {
    this.applyFilter();
  }

  private handleEvent(event: ApiLogEvent): void {
    if (event.type === 'log') {
      if (this.paused) return;

      const line = event.line || '';

      this.addLine({
        type: this.getLogType(line),
        text: line,
        time: this.getTime(event.receivedAt),
      });

      return;
    }

    if (event.type === 'info') {
      this.addLine({
        type: 'info',
        text: event.file
          ? `${event.message || 'Info'} — ${event.file}`
          : event.message || 'Conectado',
        time: this.getTime(event.receivedAt),
      });

      return;
    }

    if (event.type === 'error') {
      this.connected = false;

      this.addLine({
        type: 'error',
        text: event.message || 'Error en el stream de logs',
        time: this.getTime(event.receivedAt),
      });

      return;
    }

    if (event.type === 'close') {
      this.connected = false;

      this.addLine({
        type: 'warning',
        text: event.message || 'Stream cerrado',
        time: this.getTime(event.receivedAt),
      });
    }
  }

  private addLine(line: ViewLogLine): void {
    this.logs = [...this.logs, line].slice(-1000);
    this.applyFilter();
    this.shouldScroll = true;
  }

  private applyFilter(): void {
    const q = this.filterText.trim().toLowerCase();

    if (!q) {
      this.filteredLogs = this.logs;
      return;
    }

    this.filteredLogs = this.logs.filter((item) =>
      item.text.toLowerCase().includes(q)
    );
  }

  private getLogType(line: string): ViewLogLine['type'] {
    const text = line.toLowerCase();

    if (
      text.includes('error') ||
      text.includes('err') ||
      text.includes('exception') ||
      text.includes('failed') ||
      text.includes('❌')
    ) {
      return 'error';
    }

    if (
      text.includes('warn') ||
      text.includes('warning') ||
      text.includes('unauthorized') ||
      text.includes('401') ||
      text.includes('403')
    ) {
      return 'warning';
    }

    if (
      text.includes('success') ||
      text.includes('ok') ||
      text.includes('200') ||
      text.includes('201') ||
      text.includes('✅')
    ) {
      return 'success';
    }

    return 'normal';
  }

  private getTime(date = new Date()): string {
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
}